/**
 * A startup picker's options.
 *
 * A minimal `/startups` read for a select — id and name only. Lives in `lib/`
 * because the matchmaker feature needs it and plan §3.1 forbids one feature
 * importing another feature's api. It is deliberately narrow: a picker does not
 * need the filtering, sorting or derived fields the startups list carries, so
 * this does not re-export that feature's richer hooks.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.18, §3.1
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { StartupSummary } from '@nexus/contracts';

import { api } from './api-client';
import type { PageMeta } from './pagination';
import { queryKeys } from './query-keys';

interface StartupPage {
  data: StartupSummary[];
  meta: PageMeta;
}

/** An option for a startup picker. */
export interface StartupOption {
  id: string;
  name: string;
}

/**
 * Every startup as an id/name option, for a picker.
 *
 * A generous page size so the whole pipeline is choosable without paging — a
 * picker that only offered a recent page would hide the very startup a user
 * came to match.
 */
export function useStartupOptions(): UseQueryResult<StartupOption[]> {
  return useQuery({
    queryKey: [...queryKeys.startups.lists(), 'options'],
    queryFn: async () => {
      const page = await api.get<StartupPage>('/startups', {
        query: { page: 1, pageSize: 200 },
      });
      return page.data.map((startup) => ({ id: startup.id, name: startup.name }));
    },
  });
}
